import {
  enumWeightDisplayUnit,
  PurchaseOrderSortOption,
  PurchaseOrderStatus,
} from '@rematter/frontend-lib-api-client';

import { PurchaseOrderRecord } from '../types/purchaseOrder';
import { getSalesOrderLineItemsTotals } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const purchaseOrdersListQueryAtom = createQueryAtomFactory(
  'trade.purchaseOrdersList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    facilityId?: string;
    accountIds?: string[];
    limit?: number;
    offset?: number;
    sorting?: PurchaseOrderSortOption[];
    status?: PurchaseOrderStatus[];
    includeLineItems?: boolean;
    fromEndDate?: string;
    toEndDate?: string;
  }) => ({
    purchaseOrders: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          facilityIds: params.facilityId ? [params.facilityId] : undefined,
          status: params.status,
          includeLineItems: params.includeLineItems ?? true,
          accountIds: params.accountIds,
          fromEndDate: params.fromEndDate,
          toEndDate: params.toEndDate,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        startDate: true,
        endDate: true,
        counterpartyReferenceNumber: true,
        paymentCurrency: true,
        freightTerm: true,
        paymentTerm: true,
        notes: true,
        ...(params.includeLineItems
          ? {
              lineItems: {
                id: true,
                unit: true,
                quantity: true,
                allocatedQuantity: true,
                receivedQuantity: true,
                allocatedPercentage: true,
                fulfilledPercentage: true,
                unitPrice: {
                  amount: true,
                  currencyCode: true,
                },
                totalPrice: {
                  amount: true,
                  currencyCode: true,
                },
                itemId: true,
              },
            }
          : {}),
        allocatedPercentage: true,
        fulfilledPercentage: true,
        account: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      purchaseOrders: PurchaseOrderRecord[];
      totalCount: number;
    } => {
      if (!data || !data.purchaseOrders || !data.purchaseOrders.entries) {
        return { purchaseOrders: [], totalCount: 0 };
      }

      if (!data.purchaseOrders.entries.length) {
        return { purchaseOrders: [], totalCount: 0 };
      }

      // TODO: add support for weightDisplayUnit in purchaseOrder
      const weightDisplayUnit = enumWeightDisplayUnit.ScaleUnit;

      return {
        purchaseOrders: data.purchaseOrders.entries.map((purchaseOrder) => {
          const lineItems = purchaseOrder.lineItems || [];

          const { totalNetWeight, totalPrice, totalEachCount } =
            getSalesOrderLineItemsTotals(lineItems, weightDisplayUnit);

          const totalAllocated = lineItems.reduce(
            (sum, item) => sum + (item.allocatedQuantity || 0),
            0,
          );
          const totalFulfilled = lineItems.reduce(
            (sum, item) => sum + (item.receivedQuantity || 0),
            0,
          );

          return {
            id: purchaseOrder.id,
            documentNumber: purchaseOrder.documentNumber,
            status: purchaseOrder.status as PurchaseOrderStatus,
            startDate: maybeParseCalendarDate(purchaseOrder.startDate),
            endDate: maybeParseCalendarDate(purchaseOrder.endDate),
            counterpartyReferenceNumber:
              purchaseOrder.counterpartyReferenceNumber || null,
            paymentCurrency: purchaseOrder.paymentCurrency || null,
            freightTerm: purchaseOrder.freightTerm || null,
            paymentTerm: purchaseOrder.paymentTerm || null,
            notes: purchaseOrder.notes || null,
            completionNotes: null,
            lineItemCount: lineItems.length,
            totalNetWeight,
            totalPrice,
            totalEachCount,
            totalAllocated,
            totalFulfilled,
            allocatedPercentage: purchaseOrder.allocatedPercentage ?? null,
            fulfilledPercentage: purchaseOrder.fulfilledPercentage ?? null,
            account: purchaseOrder.account,
            contact: null,
            facility: purchaseOrder.facility,
            accountRepresentative: null,
            accountAddress: null,
            supplierName: purchaseOrder.account.name,
            timestamps: {
              createdAt: parseZonedDateTime(purchaseOrder.timestamps.createdAt),
              updatedAt: parseZonedDateTime(purchaseOrder.timestamps.updatedAt),
            },
          } as PurchaseOrderRecord;
        }),
        totalCount: data.purchaseOrders.page?.total || 0,
      };
    },
  },
);
